import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Image as ImageIcon, Loader2, Sparkles, Link as LinkIcon, Globe, Building2, Monitor, Search, Check, Layers, AlertCircle, ArrowLeft, Database, Info, CloudUpload, PenLine } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { uploadSharedImage, supabase } from '../../services/authService';
import { pointService } from '../../services/pointService';
import { analyzeFootballArticle, AIDraftResult, validateUrlPolicy, translateArticle, translateArticleMulti } from '../../services/gemini';
import { fetchPexelsImages } from '../../services/pexelsService';
import { Layout } from 'lucide-react';
import { clearCache } from '../../services/storageService';
import { AIDraftModal } from '../News/AIDraftModal';
import { ConfirmModal } from '../Common/ConfirmModal';
import { t } from '../../i18n';

interface NewsPostViewProps {
    onBack: () => void;
}

const PEXELS_KEYWORDS = [
    "soccer game action", "soccer fans", "soccer player celebration", "soccer player shooting",
    "soccer training", "soccer stadium crowd", "soccer sidelines", "soccer ball",
    "soccer strategy", "soccer fans"
];

export const NewsPostView: React.FC<NewsPostViewProps> = ({ onBack }) => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const { lang } = useLanguage();
    const { showToast } = useToast();
    
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    const searchParams = new URLSearchParams(window.location.search);
    const isFromStadium = searchParams.get('from') === 'stadium';
    const initialTeamId = searchParams.get('teamId');
    const [postType, setPostType] = useState('community');
    const [linkUrl, setLinkUrl] = useState('');
    const [sourceName, setSourceName] = useState<string | null>(null);
    const [tempLink, setTempLink] = useState('');
    const [tempSource, setTempSource] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [showSourceInput, setShowSourceInput] = useState(false);
    
    const [attachedImage, setAttachedImage] = useState<File | null>(null);
    const [pexelsImageUrl, setPexelsImageUrl] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [showImageMenu, setShowImageMenu] = useState(false);
    const [showPexelsModal, setShowPexelsModal] = useState(false);
    const [isPexelsAnimating, setIsPexelsAnimating] = useState(false);
    const [pexelsImages, setPexelsImages] = useState<any[]>([]);
    const [pexelsPage, setPexelsPage] = useState(1);
    const [loadingPexels, setLoadingPexels] = useState(false);
    const [hasMorePexels, setHasMorePexels] = useState(true);
    const [pexelsError, setPexelsError] = useState<string | null>(null);
    const [currentPexelsQuery, setCurrentPexelsQuery] = useState('');
    const MAX_PEXELS_COUNT = 100; 
    const PEXELS_BATCH_SIZE = 50; 
    
    const [isPosting, setIsPosting] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [statusMsgIdx, setStatusMsgIdx] = useState(0);
    const [isValidating, setIsValidating] = useState(false);
    const [linkError, setLinkError] = useState<string | null>(null);
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', description: '' });
    const [aiDraft, setAiDraft] = useState<AIDraftResult | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);
    
    const titleRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageMenuRef = useRef<HTMLDivElement>(null);
    const msgTimerRef = useRef<number | null>(null);

    const scoutingMessages = [t(lang, 'scoutingStatus1'), t(lang, 'scoutingStatus2'), t(lang, 'scoutingStatus3'), t(lang, 'scoutingStatus4')];

    useEffect(() => {
        if (!isAuthLoading && !user) {
            sessionStorage.setItem('redirectAfterLogin', '/feed/post');
            if (window.navigateTo) window.navigateTo('/login');
        }
    }, [user, isAuthLoading]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => { if (imageMenuRef.current && !imageMenuRef.current.contains(e.target as Node)) setShowImageMenu(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { if (titleRef.current) { titleRef.current.style.height = 'auto'; titleRef.current.style.height = `${titleRef.current.scrollHeight}px`; } }, [title]);
    useEffect(() => { if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; } }, [content]);

    useEffect(() => {
        if (isAnalyzing) {
            // 메시지 전환 주기를 5초에서 3초로 단축하여 지루함 감소
            msgTimerRef.current = window.setInterval(() => setStatusMsgIdx(prev => (prev + 1) % scoutingMessages.length), 3000);
        } else {
            if (msgTimerRef.current) clearInterval(msgTimerRef.current);
            setStatusMsgIdx(0);
        }
        return () => { if (msgTimerRef.current) clearInterval(msgTimerRef.current); };
    }, [isAnalyzing, lang]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { 
            setAttachedImage(file); 
            setPexelsImageUrl(null); 
            setPreviewUrl(URL.createObjectURL(file)); 
            setShowImageMenu(false); 
        }
    };

    const loadPexelsBatch = useCallback(async (query: string, page: number, isInitial: boolean = false) => {
        if (loadingPexels || pexelsImages.length >= MAX_PEXELS_COUNT) return;
        setLoadingPexels(true); setPexelsError(null);
        try {
            const result = await fetchPexelsImages(query, PEXELS_BATCH_SIZE, page);
            if (isInitial) setPexelsImages(result.photos);
            else setPexelsImages(prev => [...prev, ...result.photos]);
            const currentTotal = isInitial ? result.photos.length : pexelsImages.length + result.photos.length;
            setHasMorePexels(result.photos.length === PEXELS_BATCH_SIZE && currentTotal < MAX_PEXELS_COUNT);
        } catch (e) { setPexelsError(t(lang, 'errorLoadingImages')); } finally { setLoadingPexels(false); }
    }, [loadingPexels, pexelsImages.length, lang]);

    const handlePexelsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMorePexels || loadingPexels) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            const nextPage = pexelsPage + 1;
            setPexelsPage(nextPage);
            loadPexelsBatch(currentPexelsQuery, nextPage);
        }
    };

    const handleOpenPexels = () => {
        setShowImageMenu(false); setShowPexelsModal(true); setPexelsPage(1); setPexelsImages([]); setPexelsError(null);
        const randomQuery = PEXELS_KEYWORDS[Math.floor(Math.random() * PEXELS_KEYWORDS.length)];
        setCurrentPexelsQuery(randomQuery);
        setTimeout(() => { setIsPexelsAnimating(true); loadPexelsBatch(randomQuery, 1, true); }, 10);
    };

    const handleClosePexels = () => { setIsPexelsAnimating(false); setTimeout(() => { setShowPexelsModal(false); setPexelsImages([]); setPexelsError(null); }, 500); };
    
    const handleSaveLink = () => {
        if (!tempLink.trim()) return;
        if (!tempLink.startsWith('https://')) {
            setLinkError(t(lang, 'linkValidationError'));
            return;
        }
        setLinkUrl(tempLink);
        setShowLinkInput(false);
        setTempLink('');
        setLinkError(null);
    };

    const handleSaveSource = () => { if (tempSource.trim()) { setSourceName(tempSource); setShowSourceInput(false); setTempSource(''); } };

    const handleAIAnalyze = async () => {
        if (!linkUrl) return;
        setIsAnalyzing(true);
        setAiDraft(null);

        // 타임아웃 45초로 단축 (Gemini Flash는 보통 20초 내외 완료)
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('TIMEOUT')), 45000)
        );

        try {
            const targetLang = lang === 'ko' ? 'Korean' : 'English';
            const result = await Promise.race([
                analyzeFootballArticle(linkUrl, targetLang),
                timeoutPromise
            ]) as AIDraftResult;
            
            if (!result.isFootball) {
                setIsAnalyzing(false);
                showToast('info', t(lang, 'aiNotFootball'));
                setLinkUrl('');
            } else {
                setAiDraft(result);
                setIsAnalyzing(false);
                setIsPreviewOpen(true);
                setTimeout(() => setIsPreviewAnimating(true), 50);
            }
        } catch (e: any) { 
            setIsAnalyzing(false);
            if (e.message === 'TIMEOUT') {
                showToast('error', t(lang, 'aiTimeout'));
            } else {
                setAlertModal({ isOpen: true, title: t(lang, 'analysisFailed'), description: e.message }); 
            }
        }
    };

    const handlePost = async () => {
        // 팬텀 스타디움 게시물은 제목(title)이 없어도 됨
        if (!user || !content.trim() || (!isFromStadium && !title.trim())) return;

        // 관리자인 경우 게시 버튼 누르면 타입 선택 모달 표시 (스타디움 게시물 제외)
        if (user.role === 'admin' && !showTypeSelector && !isFromStadium) {
            setShowTypeSelector(true);
            return;
        }

        setIsPosting(true);
        try {
            let finalImageUrl = pexelsImageUrl || null;
            if (attachedImage) {
                try {
                    finalImageUrl = await uploadSharedImage(user.id, attachedImage);
                } catch (imgError) {
                    console.error("Image upload failed:", imgError);
                }
            }

            const publishedAt = new Date().toISOString();
            
            // 1. 초기 페이로드 구성 (작성 언어 우선)
            const postPayload: any = {
                type: isFromStadium ? 'stadium' : (postType || aiDraft?.category || 'community'),
                source: isFromStadium ? 'Stadium' : ((postType === 'magazine' || postType === 'webtoon') ? postType : (sourceName || (pexelsImageUrl ? 'Pexels' : (linkUrl ? 'Link' : 'XLEVEN')))),
                url: linkUrl || null,
                image: finalImageUrl,
                published_at: publishedAt,
                transfer_status: aiDraft?.transferStatus || null,
                importance: aiDraft?.importance || 'c',
                players: aiDraft?.players || [],
                teams: isFromStadium ? (initialTeamId ? [initialTeamId] : []) : (aiDraft?.teams || (initialTeamId ? [initialTeamId] : [])),
                user_id: user.id,
                // 작성 시점의 언어 필드 우선 채움
                [`title_${lang}`]: isFromStadium ? 'Stadium Post' : title.trim(),
                [`content_${lang}`]: content.trim(),
            };

            // 2. 초기 게시물 삽입 (빠른 리다이렉션을 위해 번역 대기 안 함)
            const { data: insertedData, error: postError } = await supabase
                .from('news_list')
                .insert([postPayload])
                .select()
                .single();
            
            if (postError) throw postError;

            // 3. 백그라운드 번역 및 업데이트 (비동기)
            const performBackgroundTranslation = async (newsId: string) => {
                try {
                    const translations = await translateArticleMulti(title, content, false);
                    if (translations) {
                        const updatePayload = {
                            title_ko: translations.ko?.title?.trim() || title.trim(),
                            title_en: translations.en?.title?.trim() || title.trim(),
                            // title_ja: translations.ja?.title?.trim() || title.trim(),
                            // title_es: translations.es?.title?.trim() || title.trim(),
                            // title_fr: translations.fr?.title?.trim() || title.trim(),
                            content_ko: translations.ko?.content?.trim() || content.trim(),
                            content_en: translations.en?.content?.trim() || content.trim(),
                            // content_ja: translations.ja?.content?.trim() || content.trim(),
                            // content_es: translations.es?.content?.trim() || content.trim(),
                            // content_fr: translations.fr?.content?.trim() || content.trim(),
                        };
                        await supabase.from('news_list').update(updatePayload).eq('id', newsId);
                    }
                } catch (transError) {
                    console.error("Background translation failed:", transError);
                }
            };

            // Promise에 await를 걸지 않고 백그라운드에서 실행
            if (insertedData?.id) {
                performBackgroundTranslation(insertedData.id);
            }

            // 4. 포인트 적립: 피드 등록 시 10 포인트 (일 최대 100)
            try {
                const pointResult = await pointService.awardPointsWithLimit(
                    user.id,
                    10,
                    'News Feed Post',
                    100,
                );
                if (pointResult.success && pointResult.pointsEarned > 0) {
                    showToast(
                        'success',
                        t(lang, 'feedPostPointEarned').replace('{{amount}}', String(pointResult.pointsEarned))
                    );
                } else {
                    showToast('success', t(lang, 'postSuccess'));
                }
            } catch (pError) {
                console.error("Point awarding failed:", pError);
                showToast('success', t(lang, 'postSuccess'));
            }

            clearCache();
            // 약간의 딜레이 후 이동하여 토스트 메시지를 볼 수 있게 함
            setTimeout(() => {
                if (isFromStadium && initialTeamId) {
                    if (window.navigateTo) window.navigateTo(`/community/stadium/${initialTeamId}`);
                    else onBack();
                } else if (window.navigateTo) {
                    window.navigateTo('/feed');
                } else {
                    onBack();
                }
            }, 1500);
        } catch (e: any) {
            console.error("Post submission error:", e);
            showToast('error', e.message || t(lang, 'failedToPostNews'));
        } finally {
            setIsPosting(false);
            setShowTypeSelector(false);
        }
    };

    if (isAuthLoading || !user) return null;

    return (
        <div className="min-h-screen bg-white flex flex-col relative">
            {isPosting && (
                <div className="fixed inset-0 z-[500] flex flex-col items-center justify-center bg-white/60 backdrop-blur-xl">
                    <div className="relative mb-6">
                        <PenLine className="text-rose-500 animate-[bounce_1s_infinite]" size={40} />
                        <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-rose-200/50 scale-x-0 animate-[grow-shrink_1s_infinite]"></div>
                    </div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tighter">{t(lang, 'uploadingIntel')}</h3>
                </div>
            )}
            {isAnalyzing && (
                <div className="fixed inset-0 z-[220] flex flex-col items-center justify-center bg-white/95 backdrop-blur-xl">
                    <Sparkles size={40} className="text-rose-500 animate-pulse mb-6" />
                    <h3 className="text-xl font-black text-slate-900 uppercase">{t(lang, 'aiAnalyzing')}</h3>
                    <p className="text-[10px] font-black uppercase text-slate-400 mt-4">{scoutingMessages[statusMsgIdx]}</p>
                </div>
            )}

            <ConfirmModal 
                isOpen={alertModal.isOpen} 
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })} 
                onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                description={alertModal.description}
                confirmLabel={t(lang, 'close')}
                cancelLabel=""
            />

            <ConfirmModal 
                isOpen={showExitConfirm} 
                onClose={() => setShowExitConfirm(false)} 
                onConfirm={onBack}
                title={t(lang, 'postCancel')}
                description={t(lang, 'postCancelDesc')}
                confirmLabel={t(lang, 'confirm')}
                cancelLabel={t(lang, 'cancel')}
            />

            <ConfirmModal
                isOpen={showTypeSelector}
                onClose={() => setShowTypeSelector(false)}
                onConfirm={handlePost}
                title={t(lang, 'selectPostType')}
                description={
                    <div className="mt-4 flex flex-col gap-2">
                        <button 
                            onClick={() => setPostType('community')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'community' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Community</span>
                            {postType === 'community' && <Check size={16} />}
                        </button>
                        <button 
                            onClick={() => setPostType('magazine')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'magazine' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Magazine</span>
                            {postType === 'magazine' && <Check size={16} />}
                        </button>
                        <button 
                            onClick={() => setPostType('webtoon')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'webtoon' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Webtoon</span>
                            {postType === 'webtoon' && <Check size={16} />}
                        </button>
                    </div>
                }
                confirmLabel={t(lang, 'post')}
                cancelLabel={t(lang, 'cancel')}
            />

            <AIDraftModal isOpen={isPreviewOpen} isAnimating={isPreviewAnimating} draft={aiDraft} lang={lang} onClose={() => { setIsPreviewAnimating(false); setTimeout(() => setIsPreviewOpen(false), 500); }} onApply={() => { setTitle(aiDraft!.title); setContent(aiDraft!.generatedContent); setIsPreviewAnimating(false); setTimeout(() => setIsPreviewOpen(false), 500); }} />
            
            <header className="sticky top-0 z-40 h-[65px] px-4 flex items-center justify-between border-b border-gray-100 bg-white/95 backdrop-blur-md">
                <div className="flex items-center gap-4">
                    <button onClick={() => {
                        if (title.trim() || content.trim()) {
                            setShowExitConfirm(true);
                        } else {
                            onBack();
                        }
                    }} className="w-10 h-10 flex items-center justify-center rounded-full md:hover:bg-gray-100 transition-colors"><X size={24} className="text-rose-500" strokeWidth={3} /></button>
                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight">{isFromStadium ? t(lang, 'stadiumPostTitle') : t(lang, 'shareIntelligence')}</h2>
                </div>
                <button onClick={handlePost} disabled={isPosting || !content.trim() || (!isFromStadium && !title.trim())} className="bg-slate-900 text-white px-6 py-2 rounded-full font-black text-xs uppercase tracking-widest disabled:opacity-20 transition-all">{t(lang, 'post')}</button>
            </header>

            <main className="flex-1 overflow-y-auto p-4 md:p-8 pb-32">
                <div className="max-w-[600px] mx-auto flex gap-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-50 shrink-0"><img src={user?.avatar} className="w-full h-full object-cover" /></div>
                    <div className="flex-1 pt-0">
                        {!isFromStadium && (
                            <textarea ref={titleRef} placeholder={t(lang, 'newsTitle')} rows={1} className="w-full bg-transparent border-none focus:ring-0 text-2xl font-black text-slate-900 mb-2 outline-none uppercase tracking-tight resize-none overflow-hidden" value={title} onChange={(e) => setTitle(e.target.value)} />
                        )}
                        <textarea ref={textareaRef} placeholder={isFromStadium ? t(lang, 'stadiumPostPlaceholder') : t(lang, 'whatsHappening')} className="w-full bg-transparent border-none focus:ring-0 text-lg md:text-xl font-medium text-slate-800 resize-none min-h-[150px] outline-none" value={content} onChange={(e) => setContent(e.target.value)} />
                        
                        {previewUrl && (
                            <div className="relative mt-4">
                                <img src={previewUrl} className="w-full rounded-3xl border border-gray-100 shadow-sm" alt="" />
                                <button onClick={() => { 
                                    setPreviewUrl(null); 
                                    setAttachedImage(null); 
                                    setPexelsImageUrl(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }} className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full md:hover:bg-rose-50 transition-all"><X size={16} strokeWidth={3} /></button>
                            </div>
                        )}

                        <div className="space-y-3 my-6">
                            {showLinkInput && (
                                <div className="p-2 bg-slate-50 rounded-2xl border border-rose-100 flex items-center gap-3 animate-in slide-in-from-top-2">
                                    <Globe size={18} className="text-rose-500" />
                                    <input type="text" placeholder={t(lang, 'linkPlaceholder')} className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-black text-rose-600 outline-none" value={tempLink} onChange={(e) => setTempLink(e.target.value)} />
                                    <button onClick={handleSaveLink} className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">{isValidating ? <Loader2 className="animate-spin" size={14}/> : t(lang, 'save')}</button>
                                </div>
                            )}
                            
                            {showSourceInput && (
                                <div className="p-2 bg-slate-50 rounded-2xl border border-indigo-100 flex items-center gap-3 animate-in slide-in-from-top-2">
                                    <Building2 size={18} className="text-indigo-500" />
                                    <input type="text" placeholder={t(lang, 'sourcePlaceholder')} className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-black text-indigo-600 outline-none" value={tempSource} onChange={(e) => setTempSource(e.target.value)} />
                                    <button onClick={handleSaveSource} className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">{t(lang, 'save')}</button>
                                </div>
                            )}

                            {(linkUrl || sourceName) && (
                                <div className="flex flex-wrap gap-2">
                                    {linkUrl && (
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 text-rose-600 rounded-full text-[10px] font-black border border-rose-100">
                                            <Globe size={12} />
                                            <span className="truncate max-w-[150px]">{linkUrl}</span>
                                            <button onClick={() => setLinkUrl('')}><X size={12} strokeWidth={4}/></button>
                                        </div>
                                    )}
                                    {sourceName && (
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black border border-indigo-100">
                                            <Building2 size={12} />
                                            <span>{sourceName}</span>
                                            <button onClick={() => setSourceName(null)}><X size={12} strokeWidth={4}/></button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {linkError && <p className="text-[10px] text-rose-500 font-bold px-2">{linkError}</p>}
                        </div>

                        <div className="flex items-center gap-2 mt-8 pt-4 border-t border-gray-50 relative" ref={imageMenuRef}>
                            <button onClick={() => setShowImageMenu(!showImageMenu)} className={`p-2 transition-colors ${showImageMenu ? 'text-rose-500' : 'text-slate-400 md:hover:text-rose-500'}`}><ImageIcon size={24} /></button>
                            {showImageMenu && (
                                <div className="absolute bottom-full left-0 mb-4 w-44 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-50">
                                    <div className="p-1.5 flex flex-col gap-1">
                                        <button onClick={() => { fileInputRef.current?.click(); setShowImageMenu(false); }} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase text-slate-700 md:hover:bg-slate-50"><Monitor size={16} className="text-indigo-500" /><span>Drive</span></button>
                                        <button onClick={handleOpenPexels} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase text-slate-700 md:hover:bg-slate-50"><Search size={16} className="text-emerald-500" /><span>Pexels</span></button>
                                    </div>
                                </div>
                            )}
                            
                        {!linkUrl && <button onClick={() => setShowLinkInput(!showLinkInput)} className={`p-2 transition-colors ${showLinkInput ? 'text-rose-500' : 'text-slate-400 md:hover:text-indigo-500'}`}><LinkIcon size={24} /></button>}
                        {!sourceName && <button onClick={() => setShowSourceInput(!showSourceInput)} className={`p-2 transition-colors ${showSourceInput ? 'text-indigo-500' : 'text-slate-400 md:hover:text-indigo-500'}`}><Building2 size={24} /></button>}

                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                        </div>
                    </div>
                </div>
            </main>

            {/* Pexels Modal */}
            {showPexelsModal && (
                <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isPexelsAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClosePexels}></div>
                    <div className={`relative bg-white w-full h-[85vh] md:h-[80vh] md:max-w-4xl rounded-t-[40px] md:rounded-[48px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPexelsAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-6 md:p-8 flex items-center justify-between border-b border-slate-100 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center"><ImageIcon size={20}/></div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'pexelsGallery')}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{t(lang, 'highFidelityAssets')}</p>
                                </div>
                            </div>
                            <button onClick={handleClosePexels} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={20} strokeWidth={3} /></button>
                        </header>
                        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar" onScroll={handlePexelsScroll}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {pexelsImages.map((img: any) => (
                                    <div 
                                        key={img.id} 
                                        className="aspect-video rounded-2xl overflow-hidden cursor-pointer group relative bg-slate-100"
                                        onClick={() => {
                                            setPexelsImageUrl(img.src.large);
                                            setPreviewUrl(img.src.large);
                                            setAttachedImage(null);
                                            handleClosePexels();
                                        }}
                                    >
                                        <img src={img.src.medium} className="w-full h-full object-cover transition-transform md:group-hover:scale-110" alt="" />
                                        <div className="absolute inset-0 bg-black/20 opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Check className="text-white" size={32} strokeWidth={3} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingPexels && (
                                <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-rose-500" size={24} /></div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
